import { z } from 'zod';
import { DEFAULT_REEL_AUDIO, DEFAULT_REEL_COVER, DEFAULT_REEL_SCENE_DESIGN, layoutTakesItems, REEL_CAMERAS, REEL_ICONS, REEL_LIMITS, REEL_SCENE_KINDS, REEL_SCENE_LAYOUTS, REEL_TEXT_ANIMATIONS, REEL_TEXT_POSITIONS, REEL_TRANSITIONS, } from './storyboard.js';
/**
 * `storyboarder` (spec 100 section 2, section 6.2, S100-6, AC-100-020): module
 * 100's AI job that proposes a storyboard from existing footage. The output is
 * a proposal the human applies, edits or discards; it is never saved by the
 * job. The provider schema is strict JSON (no free-form records), so overlay
 * texts are lists of `{language, text}`; `storyboardFromProposal` turns the
 * proposal into the body of a storyboard write, which the API validates with
 * the shared storyboard schema and `REEL_LIMITS` and the web applies as is.
 */
/** At most this many photos/videos, licensed tracks and verified facts go into one context. */
export const STORYBOARDER_LIMITS = {
    maxCandidates: 40,
    maxAudio: 20,
    maxFacts: 20,
    /** Excerpt length of a verified fact in the context. */
    factExcerptChars: 400,
    rationaleMaxChars: 500,
};
export const storyboarderInputSchema = z
    .object({
    content_item_id: z.uuid(),
    variant_id: z.uuid(),
    /** The item's version the proposal was asked for; an `apply` run commits on it. */
    base_version_id: z.uuid(),
    /**
     * `proposal` (default): the output stays a proposal the human applies in the reel editor. `apply` (E400, the
     * preparation chain): the run writes the proposal as the variant's storyboard and commits one version.
     */
    mode: z.enum(['proposal', 'apply']).optional(),
})
    .strict();
const proposalTextSchema = z
    .object({
    language: z.string().min(2).max(35),
    text: z.string().max(200),
})
    .strict();
/** A row of a `facts`, `steps` or `list` scene (E540); the icon comes from the fixed `REEL_ICONS` list or is `null`. */
const proposalItemSchema = z
    .object({
    icon: z.enum(REEL_ICONS).nullable(),
    texts: z.array(proposalTextSchema).max(10),
    subtexts: z.array(proposalTextSchema).max(10),
})
    .strict();
/**
 * One proposed scene. Since E540 (prompt v4) a scene may be a `graphic` scene
 * (no media, `asset_id` null) and carries the composition v4 design: layout,
 * second line, items, icon, camera and text animation. Enums are part of the
 * provider schema, so an unknown layout or icon is refused like any invalid output.
 */
const proposalSceneSchema = z
    .object({
    asset_id: z.string().min(1).max(64).nullable(),
    kind: z.enum(REEL_SCENE_KINDS),
    trim_start_ms: z.number().int().nullable(),
    trim_end_ms: z.number().int().nullable(),
    duration_ms: z.number().int(),
    texts: z.array(proposalTextSchema).max(10),
    text_position: z.enum(REEL_TEXT_POSITIONS),
    transition: z.enum(REEL_TRANSITIONS),
    layout: z.enum(REEL_SCENE_LAYOUTS),
    subtexts: z.array(proposalTextSchema).max(10),
    items: z.array(proposalItemSchema).max(REEL_LIMITS.maxItems),
    icon: z.enum(REEL_ICONS).nullable(),
    camera: z.enum(REEL_CAMERAS),
    text_animation: z.enum(REEL_TEXT_ANIMATIONS),
})
    .strict();
export const storyboarderOutputSchema = z
    .object({
    /** Render frame by frame with moving photos and texts (E540); `false` = one still layer per scene. */
    animated: z.boolean(),
    scenes: z.array(proposalSceneSchema).min(REEL_LIMITS.minScenes).max(REEL_LIMITS.maxScenes),
    outro: z
        .object({
        duration_ms: z.number().int(),
        show_logo: z.boolean(),
        texts: z.array(proposalTextSchema).max(10),
    })
        .strict(),
    /** A track of the licensed audio list, or `null` for a silent reel. */
    audio_asset_id: z.string().min(1).max(64).nullable(),
    /** Verified knowledge items the overlay texts rely on (may be empty when the texts state no fact). */
    knowledge_item_ids: z.array(z.string().min(1).max(64)).max(STORYBOARDER_LIMITS.maxFacts),
    rationale: z.string().min(1).max(STORYBOARDER_LIMITS.rationaleMaxChars),
})
    .strict();
export const storyboarderLimits = () => ({
    min_scenes: REEL_LIMITS.minScenes,
    max_scenes: REEL_LIMITS.maxScenes,
    scene_min_ms: REEL_LIMITS.sceneMinMs,
    photo_max_ms: REEL_LIMITS.photoMaxMs,
    outro_min_ms: REEL_LIMITS.outroMinMs,
    outro_max_ms: REEL_LIMITS.outroMaxMs,
    total_min_ms: REEL_LIMITS.totalMinMs,
    total_max_ms: REEL_LIMITS.totalMaxMs,
    transition_overlap_ms: REEL_LIMITS.transitionMs,
    text_max_chars: REEL_LIMITS.textMaxChars,
    subtext_max_chars: REEL_LIMITS.subtextMaxChars,
    max_items: REEL_LIMITS.maxItems,
});
const textsRecord = (texts) => Object.fromEntries((texts ?? []).filter((entry) => entry.text.trim() !== '').map((entry) => [entry.language, entry.text]));
/** Media of a scene: a graphic scene has none and no trims; a video keeps its trim. Proposals before prompt v4 lack the E540 fields (read with the defaults). */
function sceneMedia(scene) {
    if (scene.kind === 'graphic')
        return { asset_id: null };
    if (scene.kind === 'photo')
        return { asset_id: scene.asset_id };
    const start = scene.trim_start_ms ?? 0;
    return { asset_id: scene.asset_id, trim_start_ms: start, trim_end_ms: scene.trim_end_ms ?? start + scene.duration_ms };
}
function sceneDesign(scene) {
    const layout = scene.layout ?? DEFAULT_REEL_SCENE_DESIGN.layout;
    return {
        layout,
        subtexts: textsRecord(scene.subtexts),
        items: layoutTakesItems(layout) ? (scene.items ?? []).map((item) => ({ icon: item.icon, texts: textsRecord(item.texts), subtexts: textsRecord(item.subtexts) })) : [],
        icon: scene.icon ?? null,
        camera: scene.camera ?? DEFAULT_REEL_SCENE_DESIGN.camera,
        text_animation: scene.text_animation ?? DEFAULT_REEL_SCENE_DESIGN.text_animation,
    };
}
/**
 * The proposal as the body of a storyboard write (`POST …/storyboard` or
 * `PUT /v1/storyboards/{id}`, mode `storyboard`): texts as records (empty
 * texts dropped), photos without trims, music with the default mix, the cover
 * the first frame. The API checks it with `storyboardModeInputSchema` and the
 * storyboard rules; the web saves it unchanged when the human applies it.
 */
export function storyboardFromProposal(output) {
    return {
        mode: 'storyboard',
        animated: output.animated ?? false,
        scenes: output.scenes.map((scene) => ({
            ...sceneMedia(scene),
            kind: scene.kind,
            duration_ms: scene.duration_ms,
            crop: null,
            texts: textsRecord(scene.texts),
            text_position: scene.text_position,
            transition: scene.transition,
            ...sceneDesign(scene),
        })),
        outro: { duration_ms: output.outro.duration_ms, show_logo: output.outro.show_logo, texts: textsRecord(output.outro.texts) },
        audio: output.audio_asset_id ? { asset_id: output.audio_asset_id, ...DEFAULT_REEL_AUDIO } : null,
        cover: { ...DEFAULT_REEL_COVER },
    };
}
//# sourceMappingURL=storyboarder.js.map